import AsyncStatus from 'mini-common-schema/lib/schema/AsyncStatus';
import getCurrentSelectedTableView from 'mini-mail-list-store/lib/utils/getSelectedTableView';
import SearchTableQuery from 'mini-mail-list-store/lib/store/schema/SearchTableQuery';
import { appendRowResponse } from 'mini-mail-list-response-processor';
import { getCurrentQuery } from 'mini-3s-search-store/lib/selectors/getCurrentQuery';
import { getMailSearchResults } from '../utils/getMailSearchResults';
import { getMailSearchStore } from '../store/Store';
import { getSearchResultsLogicalId } from 'mini-3s-search-store/lib/selectors/getSearchResultsLogicalId';
import { logMailResultsView } from '../utils/logMailResultsView';
import { orchestrator } from 'satcheljs';
import { setIsLoadingForCurrentTable } from 'mini-mail-list-actions/lib/actions/tables/setIsLoadingForCurrentTable';
import { setTermsToHighlightInCurrentTable } from 'mini-mail-list-actions/lib/actions/tables/setTermsToHighlightInCurrentTable';

import {
    executeMailSearch,
    updateCurrentTableValuesForSearchComplete,
    setSearchSource,
    setAsyncStatus,
} from '../actions/privateActions';

export const MORE_SEARCH_RESULT_DELTA = 1000;

/**
 * This orchestrator triggers fetch of query result and then triggers an action to process the response
 */
export default orchestrator(executeMailSearch, async () => {
    const currentSelectedTableView = getCurrentSelectedTableView();
    const searchTableQuery = currentSelectedTableView?.tableQuery as SearchTableQuery;

    /** set loading to true before the process starts */
    setIsLoadingForCurrentTable(true);
    setAsyncStatus(AsyncStatus.Loading);

    /*
     * Get the terms to highlight
     * Don't replace the termsToHighlight if we already have them, since
     * we don't want to re-render all items in the listView, and the
     * search terms shouldn't change within a query.
     */
    const userQueryString = getCurrentQuery();
    const searchTerms = userQueryString ? userQueryString.split(' ') : null;
    if (!currentSelectedTableView?.termsToHighlight) {
        setTermsToHighlightInCurrentTable(searchTerms);
    }

    /**
     * Get the search results based on fallback, if required.
     * It'll fallback only if it's an initial search
     */
    const { searchSource } = getMailSearchStore();
    const isInitialSearch = !currentSelectedTableView?.isInitialLoadComplete;
    const {
        asyncStatus,
        rows,
        areMoreResultsAvailable,
        searchSource: searchSourceUsed,
    } = await getMailSearchResults(searchSource, isInitialSearch, searchTableQuery);

    /**
     * Set the used source in store, so that next time it can be used directly
     */
    setSearchSource(searchSourceUsed);

    /** Check if the selectedTableView has changed and if so, return */
    if (currentSelectedTableView?.id !== getCurrentSelectedTableView()?.id) {
        return;
    }

    /** If the selectedTableView is still the same, continue with the searchResponse */
    if (asyncStatus === AsyncStatus.Error || asyncStatus === AsyncStatus.ServerError) {
        updateCurrentTableValuesForSearchComplete(true /**isError */);
        setIsLoadingForCurrentTable(false);
        setAsyncStatus(asyncStatus);
        return;
    }

    /**
     * When moreResultsAreAvailable, we're not sure how many more are there, hence we need
     * to keep the totalResultsCount as an arbitrarily high value. This is important for selectAll and loadMore.
     */
    const { itemIds } = currentSelectedTableView;
    const totalResultsCount = areMoreResultsAvailable
        ? itemIds.length + MORE_SEARCH_RESULT_DELTA
        : itemIds.length;

    appendRowResponse(currentSelectedTableView, rows, totalResultsCount);

    /** update the table for loadedIndex, isInitialLoad and hasErrorLoadingTable values */
    updateCurrentTableValuesForSearchComplete(false /**isError */);

    /** Set isLoading to false, once everything is done */
    setIsLoadingForCurrentTable(false);
    setAsyncStatus(asyncStatus);

    logMailResultsView(getSearchResultsLogicalId());
});
